// §4.17 MERGE, /sales/customers/$customerId/merge (Module 09, P3-E2 PR E2-2b;
// spec 09 §3.13, §4.17, D-324 rulings 4 to 16).
//
// The customer in the address STAYS (the winner). The admin picks the other
// customer, the one merged in, and the page reads the preview (§3.13) and
// hands it to the panel, which holds every choice and the Confirm
// (`components/merge/merge-panel.tsx`).
//
// WHO SEES THE PAGE (§7 narrowing 18). A caller on the admin lineage, the
// archive's rule, offered on `isAdmin` as the 360's Merge is
// (`canMergeCustomers`). Anyone else reaching the address reads a forbidden
// page, and while the role feed is in flight or failed the page lets the
// server decide (`isSalesReadRefused`'s fail-open rule).
//
// DESKTOP ONLY (§4.17's phone line). Below 769 px the page is one sentence,
// "Merging two customers needs a wider screen", and it starts no read at all:
// neither the winner, nor the search, nor the preview. A two-column
// comparison of thirteen fields beside three tables does not fit a phone, and
// a merge is an admin's deliberate act at a desk.
//
// THE STATES (§4.17). The winner is read once through the 360's own GET (its
// name heads the page and its status decides whether a merge can start). The
// preview has a skeleton while it reads, a customer the reader cannot open
// reads as such with a way to pick another, any other failure shows its
// request id and Try again, and offline turns Confirm off (the panel). An
// archived winner starts no merge, because the merge refuses an archived
// customer on either side (`MERGE_CUSTOMER_ARCHIVED`).
//
// THE PICKER offers ACTIVE customers only and never the winner. An archived
// one would be refused by the merge, so offering it would be offering a
// refusal. The search is the book's own read with `status=active`, debounced,
// starting at two characters, the deal dialog's customer picker in shape.

import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { MergePanel } from "../components/merge/merge-panel";
import { useMergePreview } from "../hooks/merge-queries";
import { canMergeCustomers, isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useCustomer360, useCustomers } from "../hooks/sales-queries";
import "../components/merge/merge.css";

/** The search starts at two typed characters (the deal dialog's rule). */
const PICK_MIN = 2;

export function SalesCustomerMergeScreen() {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const desktop = useDesktop();
  const params = useParams({ strict: false });
  const winnerId = typeof params.customerId === "string" ? params.customerId : "";
  const refused = isSalesReadRefused(perms, canMergeCustomers(perms));

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.merge.forbidden")} />;
  }
  if (!desktop) {
    return (
      <section className="sales-merge" data-testid="sales-merge-screen">
        <PageHeader
          title={t("sales.merge.title")}
          crumbs={[{ label: t("sales.c360.crumbRoot"), to: "/sales/customers" }]}
        />
        <p className="sales-hint" role="status" data-testid="sales-merge-narrow">
          {t("sales.merge.narrow")}
        </p>
      </section>
    );
  }
  return <MergeDesk winnerId={winnerId} />;
}

function MergeDesk({ winnerId }: { winnerId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const winner = useCustomer360(winnerId);
  const [loserId, setLoserId] = useState<string | undefined>(undefined);
  const winnerActive = winner.data?.customer.status === "active";
  const preview = useMergePreview(winnerId, loserId, winnerActive);

  if (winner.error instanceof UnauthorizedError) {
    void navigate({ to: "/login" });
    return null;
  }
  if (winner.fetchStatus === "paused" && winner.data === undefined) {
    return <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />;
  }
  if (winner.isPending) {
    return <CardSkeleton lines={6} label={t("sales.common.loading")} />;
  }
  if (winner.error !== null) {
    return (
      <section className="sales-merge" data-testid="sales-merge-screen">
        {winner.error instanceof RequestError && winner.error.httpStatus === 404 ? (
          <p className="sales-hint" role="status" data-testid="sales-merge-not-found">
            {t("sales.merge.previewNotFound")}
          </p>
        ) : (
          <ErrorState
            title={t("sales.merge.winnerError")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(winner.error)}
            onRetry={() => {
              void winner.refetch();
            }}
          />
        )}
      </section>
    );
  }

  const customer = winner.data.customer;
  return (
    <section className="sales-merge" data-testid="sales-merge-screen" data-winner={customer.id}>
      <PageHeader
        title={t("sales.merge.title")}
        crumbs={[
          { label: t("sales.c360.crumbRoot"), to: "/sales/customers" },
          { label: customer.display_name, to: `/sales/customers/${customer.id}` },
        ]}
        lede={t("sales.merge.intro", { name: customer.display_name })}
        data-testid="sales-merge-pagehead"
      />

      {!winnerActive ? (
        <p className="sales-error-note" role="alert" data-testid="sales-merge-winner-archived">
          {t("sales.merge.winnerArchived")}
        </p>
      ) : loserId === undefined ? (
        <LoserPicker
          winnerId={customer.id}
          onPick={(id) => {
            setLoserId(id);
          }}
        />
      ) : (
        <>
          <div className="sales-merge-actions">
            <Button
              size="sm"
              data-testid="sales-merge-pick-another"
              onClick={() => {
                setLoserId(undefined);
              }}
            >
              {t("sales.merge.pickAnother")}
            </Button>
          </div>
          {preview.data !== undefined ? (
            <MergePanel
              key={`${customer.id}:${loserId}`}
              preview={preview.data}
              rereading={preview.isFetching}
              mayChangeTier={perms.has("sales.tier.manage")}
              onReread={() => {
                void preview.refetch();
              }}
            />
          ) : preview.fetchStatus === "paused" ? (
            <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
          ) : preview.error !== null ? (
            <PreviewFailure
              error={preview.error}
              onRetry={() => {
                void preview.refetch();
              }}
              onPickAnother={() => {
                setLoserId(undefined);
              }}
            />
          ) : (
            <div data-testid="sales-merge-preview-loading">
              <CardSkeleton lines={8} label={t("sales.merge.previewLoading")} />
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** A preview that failed: a customer the reader cannot open (404) or the
 * same customer twice (422) says so and offers another pick; anything else
 * shows its request id and Try again (§4.17). */
function PreviewFailure({
  error,
  onRetry,
  onPickAnother,
}: {
  error: unknown;
  onRetry: () => void;
  onPickAnother: () => void;
}) {
  const { t } = useTranslation();
  if (error instanceof UnauthorizedError) return null;
  const notFound = error instanceof RequestError && error.httpStatus === 404;
  const same = error instanceof RequestError && error.code === "MERGE_SAME_CUSTOMER";
  if (notFound || same) {
    return (
      <div className="sales-merge-section" data-testid="sales-merge-preview-refused">
        <p className="sales-error-note" role="alert">
          {t(notFound ? "sales.merge.previewNotFound" : "sales.merge.previewSame")}
        </p>
        <div className="sales-merge-actions">
          <Button size="sm" onClick={onPickAnother}>
            {t("sales.merge.pickAnother")}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="sales-merge-preview-error">
      <ErrorState
        title={t("sales.merge.previewError")}
        retryLabel={t("sales.common.retry")}
        requestIdLabel={t("sales.common.requestId")}
        requestId={requestIdOf(error)}
        onRetry={onRetry}
      />
    </div>
  );
}

/** The other customer, searched among the ACTIVE customers the reader can
 * open, never the winner itself. */
function LoserPicker({ winnerId, onPick }: { winnerId: string; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const q = useDebouncedValue(text.trim(), 250);
  const searching = q.length >= PICK_MIN;
  const matches = useCustomers({ q, status: "active" }, undefined, searching);
  const options = (matches.data?.items ?? []).filter((row) => row.id !== winnerId);
  return (
    <section className="sales-merge-section" data-testid="sales-merge-pick">
      <h3>{t("sales.merge.pickTitle")}</h3>
      <div className="sales-merge-pick">
        <Field label={t("sales.merge.pickLabel")} wide>
          <input
            value={text}
            placeholder={t("sales.merge.pickHint")}
            data-testid="sales-merge-pick-q"
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
        </Field>
        {searching && options.length > 0 ? (
          <Field label={t("sales.merge.pickChoose")} wide>
            <Select
              value=""
              data-testid="sales-merge-pick-select"
              onChange={(event) => {
                if (event.target.value.length > 0) onPick(event.target.value);
              }}
            >
              <option value="">{t("sales.merge.pickChoose")}</option>
              {options.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.display_name} · {row.customer_no}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>
      {!searching ? (
        <p className="sales-hint" data-testid="sales-merge-pick-short">
          {t("sales.merge.pickShort")}
        </p>
      ) : matches.error !== null ? (
        <p className="sales-error-note" role="alert" data-testid="sales-merge-pick-failed">
          {t("sales.merge.pickFailed")}
        </p>
      ) : matches.isPending ? (
        <p className="sales-hint" role="status">
          {t("sales.common.loading")}
        </p>
      ) : options.length === 0 ? (
        <p className="sales-hint" data-testid="sales-merge-pick-none">
          {t("sales.merge.pickNone")}
        </p>
      ) : null}
    </section>
  );
}
